import { useQuery } from '@tanstack/react-query';
import { Wrench, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { listHealerActions } from '@/api/observe';
import Empty from '@/components/Empty';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { HealerAction } from '@/types';

const STRATEGY_LABEL: Record<string, string> = {
  mongo_reconnect:           'Mongo: force reconnect',
  redis_cycle:               'Redis: cycle client',
  scraper_fallback_browser:  'Scraper: enable browser fallback',
  scraper_throttle:          'Scraper: cut runs/day in half',
  scraper_pause:             'Scraper: temporary pause',
  bot_polling_backoff:       'Bot: extend polling backoff',
};

export default function Healer() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['healer-actions'],
    queryFn: () => listHealerActions(100),
    refetchInterval: 5_000,
  });

  const actions = data?.actions ?? [];

  return (
    <div className="space-y-4 max-w-5xl">
      <header>
        <h1 className="text-xl font-semibold">Auto-Healer</h1>
        <p className="text-sm text-slate-500 mt-1">
          Automated remediation log. The healer reacts to alerts and infra
          probes — flipping fallbacks on, throttling blocked scrapers, cycling
          dead clients. Manual triggers live below.
        </p>
      </header>

      {/* Strategy reference */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-sm font-medium text-slate-300 mb-2">Strategies</h2>
        <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400">
          {Object.entries(STRATEGY_LABEL).map(([k, v]) => (
            <li key={k} className="flex gap-2">
              <code className="text-slate-500 shrink-0">{k}</code>
              <span className="truncate">{v}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Actions log */}
      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-3">Recent actions</h2>
        {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {isError && <div className="text-sm text-red-300">Failed to load actions.</div>}
        {!isLoading && !isError && actions.length === 0 && (
          <Empty
            icon={<Wrench className="w-8 h-8" />}
            title="Healer has had nothing to do"
            hint="When the healer reacts to an alert, the action shows up here with its outcome."
          />
        )}
        <ul className="space-y-1">
          {actions.map(a => <HealerRow key={a.id} action={a} />)}
        </ul>
      </section>
    </div>
  );
}

function HealerRow({ action }: { action: HealerAction }) {
  const Icon = action.outcome === 'success' ? CheckCircle2
             : action.outcome === 'failed'  ? XCircle
             : MinusCircle;
  return (
    <li className="flex items-start gap-2 sm:gap-3 px-2 sm:px-3 py-2 rounded hover:bg-slate-900/60">
      <Icon className={cn(
        'w-4 h-4 shrink-0 mt-0.5',
        action.outcome === 'success' && 'text-emerald-400',
        action.outcome === 'failed'  && 'text-red-400',
        action.outcome === 'skipped' && 'text-slate-500',
      )} />
      <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xs font-mono text-slate-500 shrink-0">{action.target}</span>
        <span className="text-sm text-slate-200 break-words min-w-0">
          {STRATEGY_LABEL[action.strategy] ?? action.strategy}
          {action.detail && <span className="text-slate-500 ml-1">· {action.detail}</span>}
        </span>
      </div>
      <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap mt-0.5">{relativeTime(action.ts)}</span>
    </li>
  );
}
